import type { SendTransactionOptions, SupportedTransactionVersions, WalletName } from "@solana/wallet-adapter-base";
import { BaseWalletAdapter, WalletReadyState } from "@solana/wallet-adapter-base";
import type { Connection, Transaction, TransactionSignature } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";

import { BaseError } from "../../baseError";

class WalletForVisualizationOnlyError extends BaseError {
  constructor() {
    super("Wallet for visualization only");
  }
}

export const PublicKeyWalletName = "PublicKey" as WalletName<"PublicKey">;

export class PublicKeyWalletAdapter extends BaseWalletAdapter {
  name = PublicKeyWalletName;
  url = "https://github.com/solana-labs/wallet-adapter#usage";
  icon =
    "data:image/svg+xml;base64,PHN2ZwogICAgd2lkdGg9IjIwIgogICAgaGVpZ2h0PSIyMCIKICAgIHZpZXctYm94PSIwIDAgMTIgMTIiCiAgICBmaWxsPSJub25lIgogICAgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIgogID4KICAgIDxwYXRoCiAgICAgIGQ9Ik02LjAwMDc4IDcuMTk5ODFDNi4zMTkwNCA3LjE5OTgxIDYuNjI0MjcgNy4wNzMzOCA2Ljg0OTMxIDYuODQ4MzNDNy4wNzQzNSA2LjYyMzI5IDcuMjAwNzggNi4zMTgwNiA3LjIwMDc4IDUuOTk5OEM3LjIwMDc4IDUuNjgxNTUgNy4wNzQzNSA1LjM3NjMyIDYuODQ5MzEgNS4xNTEyOEM2LjYyNDI3IDQuOTI2MjMgNi4zMTkwNCA0Ljc5OTggNi4wMDA3OCA0Ljc5OThDNS42ODI1MiA0Ljc5OTggNS4zNzczIDQuOTI2MjMgNS4xNTIyNSA1LjE1MTI4QzQuOTI3MjEgNS4zNzYzMiA0LjgwMDc4IDUuNjgxNTUgNC44MDA3OCA1Ljk5OThDNC44MDA3OCA2LjMxODA2IDQuOTI3MjEgNi42MjMyOSA1LjE1MjI1IDYuODQ4MzNDNS4zNzczIDcuMDczMzggNS42ODI1MiA3LjE5OTgxIDYuMDAwNzggNy4xOTk4MVoiCiAgICAgIGZpbGw9IiNmZmZmZmYiCiAgICAvPgogICAgPHBhdGgKICAgICAgZmlsbC1ydWxlPSJldmVub2RkIgogICAgICBjbGlwLXJ1bGU9ImV2ZW5vZGQiCiAgICAgIGQ9Ik0wLjI3NTM5MSA1Ljk5OThDMS4wMzk3OSAzLjU2NTYgMy4zMTM3OSAxLjc5OTggNi4wMDA1OSAxLjc5OThDOC42ODczOSAxLjc5OTggMTAuOTYxNCAzLjU2NTYgMTEuNzI1OCA1Ljk5OThDMTAuOTYxNCA4LjQzNDAxIDguNjg3MzkgMTAuMTk5OCA2LjAwMDU5IDEwLjE5OThDMy4zMTM3OSAxMC4xOTk4IDEuMDM5NzkgOC40MzQwMSAwLjI3NTM5MSA1Ljk5OThaTTguNDAwNTkgNS45OTk4QzguNDAwNTkgNi42MzYzMiA4LjE0NzczIDcuMjQ2NzcgNy42OTc2NSA3LjY5Njg2QzcuMjQ3NTYgOC4xNDY5NSA2LjYzNzExIDguMzk5ODEgNi4wMDA1OSA4LjM5OTgxQzUuMzY0MDcgOC4zOTk4MSA0Ljc1MzYyIDguMTQ2OTUgNC4zMDM1MyA3LjY5Njg2QzMuODUzNDUgNy4yNDY3NyAzLjYwMDU5IDYuNjM2MzIgMy42MDA1OSA1Ljk5OThDMy42MDA1OSA1LjM2MzI5IDMuODUzNDUgNC43NTI4NCA0LjMwMzUzIDQuMzAyNzVDNC43NTM2MiAzLjg1MjY2IDUuMzY0MDcgMy41OTk4IDYuMDAwNTkgMy41OTk4QzYuNjM3MTEgMy41OTk4IDcuMjQ3NTYgMy44NTI2NiA3LjY5NzY1IDQuMzAyNzVDOC4xNDc3MyA0Ljc1Mjg0IDguNDAwNTkgNS4zNjMyOSA4LjQwMDU5IDUuOTk5OFoiCiAgICAgICBmaWxsPSIjZmZmZmZmIgogICAgLz4KICA8L3N2Zz4K";
  supportedTransactionVersions: SupportedTransactionVersions = null;
  _publicKey: PublicKey | null;
  _connected: boolean;

  constructor() {
    super();
    this._connected = false;
    this._publicKey = null;
  }

  get connecting() {
    return false;
  }

  get connected() {
    return this._connected;
  }

  get publicKey() {
    return this._publicKey;
  }

  get readyState() {
    return WalletReadyState.Loadable;
  }

  async connect(): Promise<void> {
    // connect method gets triggered on select by autoConnect in WalletProvider.
    // get the pubkey from the globally available query param.
    const searchParams = new URLSearchParams(window.location.search);
    const pubkeyParam = searchParams.get("watching");
    if (pubkeyParam)
      try {
        const publicKey = new PublicKey(pubkeyParam);
        this._publicKey = publicKey;
        this._connected = true;
        this.emit("connect", this._publicKey);
      } catch (error: any) {
        this.emit("error", error);
        throw error;
      }
  }

  async disconnect(): Promise<void> {
    this._publicKey = null;
    this._connected = false;
    this.emit("disconnect");
  }

  signTransaction() {
    return Promise.reject(new WalletForVisualizationOnlyError());
  }

  signAllTransactions(): Promise<Transaction[]> {
    return Promise.reject(new WalletForVisualizationOnlyError());
  }

  async sendTransaction(
    _transaction: Transaction,
    _connection: Connection,
    _options: SendTransactionOptions,
  ): Promise<TransactionSignature> {
    return Promise.reject(new WalletForVisualizationOnlyError());
  }
}
